import React, { useEffect, useRef, useState, useMemo } from 'react';
import { usePosts } from '../posts/hooks/usePosts';
import { Loading } from './ui/Loading';

const Tournament = () => {
  // Memoize the initial filters to prevent unnecessary re-renders
  const initialFilters = useMemo(() => ({
    category: 'tournament',
    perPage: 10, // Fetch more tournaments for slider
    orderBy: 'date',
    order: 'desc'
  }), []);

  const { posts, loading, error, fetchPosts } = usePosts({
    initialFilters,
    autoFetch: true
  });

  const [currentTournamentIndex, setCurrentTournamentIndex] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const touchStartXRef = useRef(null);

  // Auto-slide functionality
  useEffect(() => {
    if (posts.length <= 1 || !isAutoSliding) return;

    const interval = setInterval(() => {
      setCurrentTournamentIndex((prevIndex) => 
        prevIndex === posts.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [posts.length, isAutoSliding]);

  // Track viewport to toggle mobile behavior
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Pause auto-sliding on hover
  const handleMouseEnter = () => setIsAutoSliding(false);
  const handleMouseLeave = () => setIsAutoSliding(true);

  // Touch handlers for swipe navigation (mobile)
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setIsAutoSliding(false);
    if (e.touches && e.touches.length > 0) {
      touchStartXRef.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    setIsAutoSliding(true);
    if (startX == null) return;
    const endX = (e.changedTouches && e.changedTouches.length > 0) ? e.changedTouches[0].clientX : startX;
    const deltaX = endX - startX;
    const threshold = 40; // minimum swipe distance in px
    if (Math.abs(deltaX) > threshold) {
      if (deltaX < 0) {
        // swipe left -> next
        setCurrentTournamentIndex(prev => prev === posts.length - 1 ? 0 : prev + 1);
      } else {
        // swipe right -> prev
        setCurrentTournamentIndex(prev => prev === 0 ? posts.length - 1 : prev - 1);
      }
    }
  };

  const currentTournament = posts[currentTournamentIndex];

  // Parse tournament content to extract structured data
  const parseTournamentContent = (content) => {
    if (!content) return { description: '', prizePool: 'Booster Packs' };
    
    // Parse HTML content to extract different elements
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    
    // Extract different types of content
    let description = '';
    let prizePool = 'Booster Packs';
    
    // Look for quotes (blockquotes, q tags)
    const quotes = doc.querySelectorAll('blockquote, q');
    for (let quote of quotes) {
      const quoteText = quote.textContent.trim();
      // Check if this quote contains a price pattern
      const priceMatch = quoteText.match(/R\s*[\d,]+/i);
      if (priceMatch) {
        prizePool = priceMatch[0];
        break;
      }
    }
    
    // Also check for any elements that might contain price information
    if (prizePool === 'Booster Packs') {
      const allElements = doc.querySelectorAll('p, span, div, h1, h2, h3, h4, h5, h6');
      for (let element of allElements) {
        const elementText = element.textContent.trim();
        const priceMatch = elementText.match(/R\s*[\d,]+/i);
        if (priceMatch && elementText.length < 50) { // Likely a price quote if short
          prizePool = priceMatch[0];
          break;
        }
      }
    }
    
    // If no quotes found, look for any price pattern in the content
    if (prizePool === 'Booster Packs') {
      const allText = doc.body.textContent;
      const priceMatch = allText.match(/R\s*[\d,]+/i);
      if (priceMatch) {
        prizePool = priceMatch[0];
      }
    }
    
    // Extract description from paragraphs, excluding quotes
    const paragraphs = doc.querySelectorAll('p');
    const descriptionParts = [];
    
    for (let p of paragraphs) {
      const pText = p.textContent.trim();
      // Skip if this paragraph contains the price (it's likely a quote)
      if (!pText.match(/R\s*[\d,]+/i) && pText.length > 10) {
        descriptionParts.push(pText);
      }
    }
    
    // Join description parts and limit length
    description = descriptionParts.join(' ').trim();
    if (description.length > 150) {
      description = description.substring(0, 150) + '...';
    }
    
    // Fallback: if no description found, use excerpt or first part of content
    if (!description) {
      const allText = doc.body.textContent.replace(/R\s*[\d,]+/i, '').trim();
      description = allText.substring(0, 150) + (allText.length > 150 ? '...' : '');
    }
    
    return { description, prizePool };
  };

  if (loading) {
    return (
      <section className="tournament" id="tournament" style={{paddingBottom: 'var(--section-padding)', paddingTop: 0}}>
        <div className="tournament-layout">
          <div className="tournament-details-box">
            <div style={{textAlign: 'center', padding: '60px 20px'}}>
              <Loading size="lg" text="Loading tournaments..." />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="tournament" id="tournament" style={{paddingBottom: 'var(--section-padding)', paddingTop: 0}}>
        <div className="tournament-layout">
          <div className="tournament-details-box">
            <div style={{textAlign: 'center', padding: '60px 20px'}}>
              <p style={{color: '#ff6b6b', marginBottom: '20px'}}>Error loading tournaments: {error}</p>
              <button 
                onClick={() => fetchPosts({ category: 'tournament', perPage: 1, orderBy: 'date', order: 'desc' })}
                className="btn btn-primary"
                style={{
                  backgroundColor: '#14fefb',
                  color: '#1a1a1a',
                  border: 'none',
                  padding: '13px 34px',
                  fontSize: '20px',
                  fontWeight: '500',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontFamily: 'Oswald, sans-serif',
                  clipPath: 'polygon(90% 0, 100% 34%, 100% 100%, 10% 100%, 0 66%, 0 0)',
                  transition: '0.15s ease-in-out'
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!currentTournament) {
    return (
      <section className="tournament" id="tournament" style={{paddingBottom: 'var(--section-padding)', paddingTop: 0}}>
        <div className="tournament-layout">
          <div className="tournament-details-box">
            <div style={{textAlign: 'center', padding: '60px 20px'}}>
              <p style={{color: '#ccc', fontSize: '18px'}}>No tournaments available at the moment.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const { description, prizePool } = parseTournamentContent(currentTournament.content);

  return (
    <section 
      className="tournament" 
      id="tournament" 
      style={{
        paddingBottom: 'var(--section-padding)',
        paddingTop: 0
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="tournament-layout" style={{ 
        position: 'relative', 
        height: 'auto',
        overflow: 'hidden'
      }}>
        {/* Left side - Tournament info */}
        <div className="tournament-details-box">
          <div style={{
            textAlign: 'left',
            width: '100%',
            maxWidth: '400px',
            margin: '0 auto',
            padding: '0 20px'
          }}>
            <p className="tournament-subtitle" style={{
              color: '#14fefb', 
              fontSize: '18px', 
              margin: '0 0 20px 0', 
              fontFamily: 'Oswald, sans-serif', 
              fontWeight: '500', 
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>Weekly Events</p>
            
            <h2 className="h3 tournament-title" style={{
              color: 'white', 
              fontSize: '36px', 
              margin: '0 0 25px 0', 
              fontFamily: 'Oswald, sans-serif', 
              textTransform: 'uppercase', 
              lineHeight: '1.2',
              fontWeight: '600',
              letterSpacing: '0.5px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '86px',
              maxHeight: '86px'
            }}>
              {currentTournament.title}
            </h2>
            
            <p className="tournament-text" style={{
              color: '#ccc', 
              fontSize: '16px', 
              margin: '0 0 30px 0', 
              lineHeight: '1.8',
              fontFamily: 'Arial, sans-serif',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '86px',
              maxHeight: '86px'
            }}>
              {description || 'Join our weekly TCG tournaments every Saturday!'}
            </p>
            
            <div style={{ marginBottom: '30px' }}>
              <h3 className="h3 tournament-prize-title" style={{
                color: 'white', 
                fontSize: '24px', 
                margin: '0 0 15px 0', 
                fontFamily: 'Oswald, sans-serif', 
                textTransform: 'uppercase', 
                lineHeight: '1.2',
                fontWeight: '500',
                letterSpacing: '0.5px'
              }}>Prize Pool</h3>
              
              <data className="tournament-prize-data" style={{
                backgroundColor: '#14fefb',
                color: '#1a1a1a',
                padding: '12px 32px',
                borderRadius: '50px',
                fontSize: '28px',
                fontWeight: '700',
                fontFamily: 'Oswald, sans-serif',
                display: 'inline-block',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: '0 4px 15px rgba(20, 254, 251, 0.3)'
              }}>{prizePool}</data>
            </div>
            
            <a href="https://wa.me/27825655408" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{
              backgroundColor: '#14fefb',
              color: '#1a1a1a',
              border: 'none',
              padding: '13px 34px',
              fontSize: '20px',
              fontWeight: '500',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontFamily: 'Oswald, sans-serif',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              clipPath: 'polygon(90% 0, 100% 34%, 100% 100%, 10% 100%, 0 66%, 0 0)',
              transition: '0.15s ease-in-out',
              textDecoration: 'none',
              boxShadow: '0 4px 15px rgba(20, 254, 251, 0.3)'
            }}>Register Now</a>
          </div>
        </div>
        {/* Right side - Banner image */}
        <div className="tournament-banner-img" style={{
          backgroundImage: currentTournament.featuredImage ? `url(${currentTournament.featuredImage})` : `url(${require('../assets/images/tournamentbanner.jpg')})`
        }}>
        </div>

        {/* Slider Controls - Only show if more than 1 tournament */}
        {posts.length > 1 && (
          <>
            {/* Navigation Arrows */}
            {!isMobile && (
            <button
              onClick={() => setCurrentTournamentIndex(prev => prev === 0 ? posts.length - 1 : prev - 1)}
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: '#14fefb',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                zIndex: 10,
                textShadow: '0 0 10px rgba(20, 254, 251, 0.8)'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#ffffff';
                e.target.style.textShadow = '0 0 15px rgba(20, 254, 251, 1)';
                e.target.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#14fefb';
                e.target.style.textShadow = '0 0 10px rgba(20, 254, 251, 0.8)';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              ‹
            </button>
            )}
            {!isMobile && (
            <button
              onClick={() => setCurrentTournamentIndex(prev => prev === posts.length - 1 ? 0 : prev + 1)}
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: '#14fefb',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                zIndex: 10,
                textShadow: '0 0 10px rgba(20, 254, 251, 0.8)'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#ffffff';
                e.target.style.textShadow = '0 0 15px rgba(20, 254, 251, 1)';
                e.target.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#14fefb';
                e.target.style.textShadow = '0 0 10px rgba(20, 254, 251, 0.8)';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              ›
            </button>
            )}

            {/* Dots Indicator */}
            <div className="mobile-dots" style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '10px',
              zIndex: 10
            }}>
              {posts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTournamentIndex(index)}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    border: 'none',
                    background: index === currentTournamentIndex ? '#14fefb' : 'rgba(255, 255, 255, 0.5)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (index !== currentTournamentIndex) {
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentTournamentIndex) {
                      e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                    }
                  }}
                />
              ))}
            </div>

            {/* Tournament Counter */}
            <div className="mobile-counter" style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(0, 0, 0, 0.7)',
              color: '#14fefb',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: 'Oswald, sans-serif',
              zIndex: 10
            }}>
              {currentTournamentIndex + 1} / {posts.length}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Tournament;
